import { Button, Typography } from '@altify/components';
import { useContent } from '@altify/utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useProduct } from 'hooks/products/useProducts';
import useCountry from 'hooks/useCountry';
import {
  useGetCryptoAccountQuery,
  useGetPersonalDataQuery,
  useGetVaspsQuery,
} from 'store/slices/crypto-accounts-api';
import CryptoWithdrawalWrapper from '../../components/CryptoWithdrawalWrapper';
import CryptoAccountConfirmView from '../components/CryptoAccountConfirmView';
import { content } from './route.content';

export const Route = createFileRoute(
  '/_authenticated/funds/withdraw/crypto/$productCode/wallet/verify',
)({
  component: WithdrawCryptoWallet,
});

function WithdrawCryptoWallet() {
  const localizedContent = useContent(content);
  const navigate = useNavigate();
  const countries = useCountry();
  const { productCode } = Route.useParams();
  const { product } = useProduct(productCode);
  const { data: cryptoAccountData, isLoading: cryptoAccountIsLoading } =
    useGetCryptoAccountQuery(productCode!, { refetchOnMountOrArgChange: true });
  const { data: personalData, isLoading: personalDataIsLoading } =
    useGetPersonalDataQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });
  const { data: vaspsData, isLoading: isVaspsLoading } = useGetVaspsQuery();
  const isLoading =
    personalDataIsLoading ||
    cryptoAccountIsLoading ||
    isVaspsLoading ||
    !countries.length;
  const residenceCountry = countries?.find(
    c => c.code === personalData?.residencyCountryCode,
  )?.name;

  const personalAddressLabel = personalData
    ? `${personalData?.streetName} ${personalData?.streetNumber}, ${personalData?.residencyCity}, ${personalData?.zipCode}, ${residenceCountry}`
    : undefined;
  const institutionName =
    vaspsData?.find(v => v.id === cryptoAccountData?.institutionId)?.name ??
    cryptoAccountData?.name;
  const dataMissing = cryptoAccountData?.isHosted == null || !personalData;

  const bodyContent = (
    <>
      {cryptoAccountData ? (
        <CryptoAccountConfirmView
          productName={product?.name ?? ''}
          isHosted={cryptoAccountData.isHosted}
          institutionName={institutionName}
          personalAddressLabel={personalAddressLabel}
          address={cryptoAccountData.address ?? ''}
        />
      ) : (
        <div className="flex-1 items-center">
          <Typography size="large" className="mx-6">
            {localizedContent.missingAddress(product?.name ?? '')}
          </Typography>
        </div>
      )}
    </>
  );

  const buttons = (
    <>
      {!isLoading && cryptoAccountData && (
        <div className="flex flex-col items-center w-full gap-4 pb-20">
          <Button
            color="secondary"
            size="large"
            text={localizedContent.edit}
            className="w-full"
            onPress={() =>
              navigate({
                to: `/funds/withdraw/crypto/${productCode}/wallet/edit/travel-rule`,
              })
            }
          />
          <Button
            color="primary"
            size="large"
            disabled={dataMissing}
            text={localizedContent.next}
            className="w-full"
            onPress={() =>
              navigate({ to: `/funds/withdraw/crypto/${productCode}` })
            }
          />
        </div>
      )}
      {!isLoading && !cryptoAccountData && (
        <div className="pb-20 w-full">
          <Button
            className="mt-auto"
            color="primary"
            size="large"
            text={localizedContent.addAddressText(product?.name ?? '')}
            onPress={() =>
              navigate({
                to: `/funds/withdraw/crypto/${productCode}/wallet/edit/travel-rule`,
              })
            }
          />
        </div>
      )}
    </>
  );
  return (
    <CryptoWithdrawalWrapper
      isLoading={isLoading}
      title={localizedContent.title(product?.name ?? '')}
      bodyContent={bodyContent}
      buttons={buttons}
    />
  );
}
